import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Brain, AlertTriangle, CheckCircle, Target } from 'lucide-react';
import { ProcessStepDetailCard } from './ProcessStepDetailCard';
import AIRiskControlService, { AIRiskSuggestion, AIControlSuggestion } from '../../services/AIRiskControlService';
import ProcessStepRiskControlService from '../../services/ProcessStepRiskControlService';

interface ProcessStepMapping {
  processStep: {
    id: number;
    processStepId: string;
    stepNumber: number;
    processStepName: string;
    description: string;
    processStepType: string;
    frequency: string;
    volume: string;
    performingCountry: string;
    legalEntity: string;
    supportingModel: string;
    thirdPartyBusinessType: string;
  };
  riskControlMappings: Array<{
    processStepId: number;
    processStepName: string;
    processStepType: string;
    processStepDescription: string;
    stepNumber: number;
    frequency: string;
    volume: string;
    performingCountry: string;
    legalEntity: string;
    supportingModel: string;
    thirdPartyBusinessType: string;
    // Risk fields
    riskId: string | null;
    riskName: string | null;
    riskDescription: string | null;
    riskTaxonomyPath: string | null;
    inherentLikelihood: number | null;
    inherentImpact: number | null;
    inherentRiskScore: number | null;
    inherentRiskLevel: string | null;
    timeframe: string | null;
    // Control fields
    controlId: string | null;
    controlName: string | null;
    controlDescription: string | null;
    controlType: string | null;
    controlOwner: string | null;
    controlExecutor: string | null;
    controlStatus: string | null;
    controlFrequency: string | null;
    designEffectiveness: string | null;
    operatingEffectiveness: string | null;
    overallEffectiveness: string | null;
    // Status
    mappingStatus: 'COMPLETE' | 'INCOMPLETE' | 'NEEDS_RISKS' | 'NEEDS_CONTROLS';
    needsRiskMapping: boolean;
    needsControlMapping: boolean;
  }>;
  summary: {
    totalRisks: number;
    totalControls: number;
    totalMappings: number;
    completionStatus: string;
    needsAttention: boolean;
  };
}

interface EnhancedRiskControlMappingTableProps {
  mappingData: ProcessStepMapping[];
  onMappingChange: (updatedData: ProcessStepMapping[]) => void;
  projectId: string;
}

export const EnhancedRiskControlMappingTable: React.FC<EnhancedRiskControlMappingTableProps> = ({
  mappingData,
  onMappingChange,
  projectId
}) => {
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const [aiSuggesting, setAiSuggesting] = useState<Set<number>>(new Set());
  const [addingRisk, setAddingRisk] = useState<Set<number>>(new Set());
  const [addingControl, setAddingControl] = useState<Set<number>>(new Set());

  const toggleExpanded = (stepId: number) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepId)) {
      newExpanded.delete(stepId);
    } else {
      newExpanded.add(stepId);
    }
    setExpandedSteps(newExpanded);
  };

  const getProcessStepRiskLevel = (mappings: any[]): 'HIGH' | 'MEDIUM' | 'LOW' | 'CRITICAL' => {
    if (!mappings.length) return 'MEDIUM';
    
    const riskLevels = mappings.map(m => m.inherentRiskLevel).filter(Boolean);
    if (riskLevels.includes('CRITICAL')) return 'CRITICAL';
    if (riskLevels.includes('HIGH')) return 'HIGH';
    if (riskLevels.includes('MEDIUM')) return 'MEDIUM';
    return 'LOW';
  };

  const getEffectiveControlsCount = (mappings: any[]): { effective: number; total: number } => {
    const controlsWithEffectiveness = mappings.filter(m => m.overallEffectiveness);
    const effective = controlsWithEffectiveness.filter(m => m.overallEffectiveness === 'Effective').length;
    return { effective, total: controlsWithEffectiveness.length };
  };

  const convertMappingsToRisks = (mappings: any[]) => {
    const uniqueRisks = new Map();
    
    mappings.forEach(mapping => {
      if (mapping.riskId && !uniqueRisks.has(mapping.riskId)) {
        uniqueRisks.set(mapping.riskId, {
          id: mapping.riskId,
          code: mapping.riskId.split('-').slice(0, 2).join('-') || `RISK-${uniqueRisks.size + 1}`,
          name: mapping.riskName || 'Unnamed Risk',
          description: mapping.riskDescription || 'No description available',
          category: 'Operational Risk',
          riskLevel: mapping.inherentRiskLevel || 'MEDIUM',
          taxonomy: mapping.riskTaxonomyPath ? mapping.riskTaxonomyPath.split(' > ') : ['Operational Risk'],
          aiGenerated: false,
          inherentLikelihood: mapping.inherentLikelihood,
          inherentImpact: mapping.inherentImpact,
          inherentRiskScore: mapping.inherentRiskScore
        });
      }
    });
    
    return Array.from(uniqueRisks.values());
  };

  const convertMappingsToControls = (mappings: any[]) => {
    const uniqueControls = new Map();
    
    mappings.forEach(mapping => {
      if (mapping.controlId && !uniqueControls.has(mapping.controlId)) {
        uniqueControls.set(mapping.controlId, {
          id: mapping.controlId,
          code: mapping.controlId.split('-').slice(0, 2).join('-') || `CTRL-${uniqueControls.size + 1}`,
          name: mapping.controlName || 'Unnamed Control',
          description: mapping.controlDescription || 'No description available',
          type: mapping.controlType || 'Manual/Detective',
          frequency: mapping.controlFrequency || 'As Needed',
          owner: mapping.controlOwner || 'Not Assigned',
          lastTested: 'Never',
          effectiveness: mapping.overallEffectiveness || 'Needs Review',
          regulatory: [
            {
              framework: 'Bank Secrecy Act',
              reference: '31 CFR § 1020.220(a)(2)(i)',
              description: 'Customer identification program requirements'
            }
          ],
          aiGenerated: false
        });
      }
    });
    
    return Array.from(uniqueControls.values());
  };

  const handleSuggestControls = async (processStepMapping: ProcessStepMapping) => {
    setAiSuggesting(prev => new Set(prev).add(processStepMapping.processStep.id));
    
    try {
      const processStepContext = {
        id: processStepMapping.processStep.id,
        name: processStepMapping.processStep.processStepName,
        description: processStepMapping.processStep.description,
        type: processStepMapping.processStep.processStepType,
        industry: 'Financial Services',
        regulatory: ['BSA/AML', 'KYC', 'FFIEC'],
        existingRisks: convertMappingsToRisks(processStepMapping.riskControlMappings),
        existingControls: convertMappingsToControls(processStepMapping.riskControlMappings)
      };

      const risks = convertMappingsToRisks(processStepMapping.riskControlMappings);
      const suggestedControls = await AIRiskControlService.suggestControlsForRisks(processStepContext, risks);
      
      // Add AI suggested controls to the mapping data
      const newMappings = suggestedControls.map(control => ({
        processStepId: processStepMapping.processStep.id,
        processStepName: processStepMapping.processStep.processStepName,
        processStepType: processStepMapping.processStep.processStepType,
        processStepDescription: processStepMapping.processStep.description,
        stepNumber: processStepMapping.processStep.stepNumber,
        frequency: processStepMapping.processStep.frequency,
        volume: processStepMapping.processStep.volume,
        performingCountry: processStepMapping.processStep.performingCountry,
        legalEntity: processStepMapping.processStep.legalEntity,
        supportingModel: processStepMapping.processStep.supportingModel,
        thirdPartyBusinessType: processStepMapping.processStep.thirdPartyBusinessType,
        // Risk fields - inherit from existing or null
        riskId: null,
        riskName: null,
        riskDescription: null,
        riskTaxonomyPath: null,
        inherentLikelihood: null,
        inherentImpact: null,
        inherentRiskScore: null,
        inherentRiskLevel: null,
        timeframe: null,
        // Control fields from AI suggestion
        controlId: control.id,
        controlName: control.name,
        controlDescription: control.description,
        controlType: control.type,
        controlOwner: control.owner || null,
        controlExecutor: control.owner || null,
        controlStatus: 'Active',
        controlFrequency: control.frequency,
        designEffectiveness: null,
        operatingEffectiveness: null,
        overallEffectiveness: null,
        // Status
        mappingStatus: 'NEEDS_RISKS' as const,
        needsRiskMapping: true,
        needsControlMapping: false
      }));

      // Update the mapping data with new AI suggested controls
      const updatedMappingData = mappingData.map(stepMapping => {
        if (stepMapping.processStep.id === processStepMapping.processStep.id) {
          return {
            ...stepMapping,
            riskControlMappings: [
              ...stepMapping.riskControlMappings,
              ...newMappings
            ],
            summary: {
              ...stepMapping.summary,
              totalControls: stepMapping.summary.totalControls + suggestedControls.length,
              totalMappings: stepMapping.summary.totalMappings + suggestedControls.length
            }
          };
        }
        return stepMapping;
      });

      onMappingChange(updatedMappingData);
      
      // Auto-expand the step to show new suggestions
      setExpandedSteps(prev => new Set(prev).add(processStepMapping.processStep.id));
      
    } catch (error) {
      console.error('Error suggesting controls:', error);
    } finally {
      setAiSuggesting(prev => {
        const newSet = new Set(prev);
        newSet.delete(processStepMapping.processStep.id);
        return newSet;
      });
    }
  };

  const handleAddRisk = async (processStepId: number) => {
    setAddingRisk(prev => new Set(prev).add(processStepId));
    
    try {
      // Find the process step mapping
      const processStepMapping = mappingData.find(mapping => mapping.processStep.id === processStepId);
      if (!processStepMapping) {
        throw new Error('Process step not found');
      }

      const processStepContext = {
        id: processStepMapping.processStep.id,
        name: processStepMapping.processStep.processStepName,
        description: processStepMapping.processStep.description,
        type: processStepMapping.processStep.processStepType,
        industry: 'Financial Services',
        regulatory: ['BSA/AML', 'KYC', 'FFIEC']
      };

      // Call AI risk suggestion service
      const suggestedRisks = await AIRiskControlService.suggestRisksForProcessStep(processStepContext);
      
      // Save risks to database
      const savedRisks = await ProcessStepRiskControlService.saveRisks(
        processStepId, 
        suggestedRisks.map(risk => ({
          name: risk.name,
          description: risk.description,
          category: risk.category,
          riskLevel: risk.riskLevel,
          taxonomy: risk.taxonomy,
          confidence: risk.confidence,
          reasoning: risk.reasoning
        }))
      );
      
      // Add saved risks to the mapping data
      const newMappings = savedRisks.map(risk => ({
        processStepId: processStepMapping.processStep.id,
        processStepName: processStepMapping.processStep.processStepName,
        processStepType: processStepMapping.processStep.processStepType,
        processStepDescription: processStepMapping.processStep.description,
        stepNumber: processStepMapping.processStep.stepNumber,
        frequency: processStepMapping.processStep.frequency,
        volume: processStepMapping.processStep.volume,
        performingCountry: processStepMapping.processStep.performingCountry,
        legalEntity: processStepMapping.processStep.legalEntity,
        supportingModel: processStepMapping.processStep.supportingModel,
        thirdPartyBusinessType: processStepMapping.processStep.thirdPartyBusinessType,
        // Risk fields from AI suggestion
        riskId: risk.id,
        riskName: risk.name,
        riskDescription: risk.description,
        riskTaxonomyPath: risk.taxonomy.join(' > '),
        inherentLikelihood: 3,
        inherentImpact: 3,
        inherentRiskScore: 9,
        inherentRiskLevel: risk.riskLevel,
        timeframe: 'Ongoing',
        // Control fields - null for risk-only mapping
        controlId: null,
        controlName: null,
        controlDescription: null,
        controlType: null,
        controlOwner: null,
        controlExecutor: null,
        controlStatus: null,
        controlFrequency: null,
        designEffectiveness: null,
        operatingEffectiveness: null,
        overallEffectiveness: null,
        // Status
        mappingStatus: 'NEEDS_CONTROLS' as const,
        needsRiskMapping: false,
        needsControlMapping: true
      }));

      // Update the mapping data with new AI suggested risks
      const updatedMappingData = mappingData.map(stepMapping => {
        if (stepMapping.processStep.id === processStepId) {
          return {
            ...stepMapping,
            riskControlMappings: [
              ...stepMapping.riskControlMappings,
              ...newMappings
            ],
            summary: {
              ...stepMapping.summary,
              totalRisks: stepMapping.summary.totalRisks + savedRisks.length,
              totalMappings: stepMapping.summary.totalMappings + savedRisks.length
            }
          };
        }
        return stepMapping;
      });

      onMappingChange(updatedMappingData);
      
      // Auto-expand the step to show new risks
      setExpandedSteps(prev => new Set(prev).add(processStepId));
      
    } catch (error) {
      console.error('Error adding risks:', error);
      // TODO: Show user-friendly error notification
    } finally {
      setAddingRisk(prev => {
        const newSet = new Set(prev);
        newSet.delete(processStepId);
        return newSet;
      });
    }
  };

  const handleAddControl = async (processStepId: number) => {
    setAddingControl(prev => new Set(prev).add(processStepId));
    
    try {
      // Find the process step mapping
      const processStepMapping = mappingData.find(mapping => mapping.processStep.id === processStepId);
      if (!processStepMapping) {
        throw new Error('Process step not found');
      }

      const processStepContext = {
        id: processStepMapping.processStep.id,
        name: processStepMapping.processStep.processStepName,
        description: processStepMapping.processStep.description,
        type: processStepMapping.processStep.processStepType,
        industry: 'Financial Services',
        regulatory: ['BSA/AML', 'KYC', 'FFIEC'],
        existingRisks: convertMappingsToRisks(processStepMapping.riskControlMappings),
        existingControls: convertMappingsToControls(processStepMapping.riskControlMappings)
      };

      const risks = convertMappingsToRisks(processStepMapping.riskControlMappings);
      const suggestedControls = await AIRiskControlService.suggestControlsForRisks(processStepContext, risks);
      
      // Save controls to database
      const savedControls = await ProcessStepRiskControlService.saveControls(
        processStepId,
        suggestedControls.map(control => ({
          id: control.id,
          name: control.name,
          description: control.description,
          type: control.type,
          frequency: control.frequency,
          owner: control.owner,
          effectiveness: control.effectiveness,
          regulatory: control.regulatory,
          confidence: control.confidence,
          reasoning: control.reasoning
        }))
      );
      
      // Add saved controls to the mapping data
      const newMappings = savedControls.map(control => ({
        processStepId: processStepMapping.processStep.id,
        processStepName: processStepMapping.processStep.processStepName,
        processStepType: processStepMapping.processStep.processStepType,
        processStepDescription: processStepMapping.processStep.description,
        stepNumber: processStepMapping.processStep.stepNumber,
        frequency: processStepMapping.processStep.frequency,
        volume: processStepMapping.processStep.volume,
        performingCountry: processStepMapping.processStep.performingCountry,
        legalEntity: processStepMapping.processStep.legalEntity,
        supportingModel: processStepMapping.processStep.supportingModel,
        thirdPartyBusinessType: processStepMapping.processStep.thirdPartyBusinessType,
        // Risk fields - null for control-only mapping
        riskId: null,
        riskName: null,
        riskDescription: null,
        riskTaxonomyPath: null,
        inherentLikelihood: null,
        inherentImpact: null,
        inherentRiskScore: null,
        inherentRiskLevel: null,
        timeframe: null,
        // Control fields from AI suggestion
        controlId: control.id,
        controlName: control.name,
        controlDescription: control.description,
        controlType: control.type,
        controlOwner: control.owner || null,
        controlExecutor: control.owner || null,
        controlStatus: 'Active',
        controlFrequency: control.frequency,
        designEffectiveness: null,
        operatingEffectiveness: null,
        overallEffectiveness: null,
        // Status
        mappingStatus: 'NEEDS_RISKS' as const,
        needsRiskMapping: true,
        needsControlMapping: false
      }));

      // Update the mapping data with new AI suggested controls
      const updatedMappingData = mappingData.map(stepMapping => {
        if (stepMapping.processStep.id === processStepId) {
          return {
            ...stepMapping,
            riskControlMappings: [
              ...stepMapping.riskControlMappings,
              ...newMappings
            ],
            summary: {
              ...stepMapping.summary,
              totalControls: stepMapping.summary.totalControls + savedControls.length,
              totalMappings: stepMapping.summary.totalMappings + savedControls.length
            }
          };
        }
        return stepMapping;
      });

      onMappingChange(updatedMappingData);
      
      // Auto-expand the step to show new controls
      setExpandedSteps(prev => new Set(prev).add(processStepId));
      
    } catch (error) {
      console.error('Error adding controls:', error);
      // TODO: Show user-friendly error notification
    } finally {
      setAddingControl(prev => {
        const newSet = new Set(prev);
        newSet.delete(processStepId);
        return newSet;
      });
    }
  };

  const handleEditStep = (processStepId: number) => {
    console.log('Edit process step:', processStepId);
    // TODO: Implement edit step functionality
  };

  const handleViewAuditTrail = (processStepId: number) => {
    console.log('View audit trail for process step:', processStepId);
    // TODO: Implement audit trail view
  };

  const handleUpdateRisk = (stepId: number, riskId: string, updates: any) => {
    console.log('Update risk:', { stepId, riskId, updates });
    // TODO: Implement risk update functionality
  };

  const handleUpdateControl = (stepId: number, controlId: string, updates: any) => {
    console.log('Update control:', { stepId, controlId, updates });
    // TODO: Implement control update functionality
  };

  const handleDeleteRisk = async (stepId: number, riskId: string) => {
    try {
      // Delete from database
      await ProcessStepRiskControlService.deleteRisk(stepId, riskId);
      
      // Remove from local state
      const updatedMappingData = mappingData.map(stepMapping => {
        if (stepMapping.processStep.id === stepId) {
          const updatedMappings = stepMapping.riskControlMappings.filter(mapping => 
            mapping.riskId !== riskId
          );
          
          return {
            ...stepMapping,
            riskControlMappings: updatedMappings,
            summary: {
              ...stepMapping.summary,
              totalRisks: Math.max(0, stepMapping.summary.totalRisks - 1),
              totalMappings: Math.max(0, stepMapping.summary.totalMappings - 1)
            }
          };
        }
        return stepMapping;
      });
      
      onMappingChange(updatedMappingData);
      
    } catch (error) {
      console.error('Error deleting risk:', error);
      // TODO: Show user-friendly error notification
    }
  };

  const handleDeleteControl = async (stepId: number, controlId: string) => {
    try {
      // Delete from database
      await ProcessStepRiskControlService.deleteControl(stepId, controlId);
      
      // Remove from local state
      const updatedMappingData = mappingData.map(stepMapping => {
        if (stepMapping.processStep.id === stepId) {
          const updatedMappings = stepMapping.riskControlMappings.filter(mapping => 
            mapping.controlId !== controlId
          );
          
          return {
            ...stepMapping,
            riskControlMappings: updatedMappings,
            summary: {
              ...stepMapping.summary,
              totalControls: Math.max(0, stepMapping.summary.totalControls - 1),
              totalMappings: Math.max(0, stepMapping.summary.totalMappings - 1)
            }
          };
        }
        return stepMapping;
      });
      
      onMappingChange(updatedMappingData);
      
    } catch (error) {
      console.error('Error deleting control:', error);
      // TODO: Show user-friendly error notification
    }
  };

  if (!mappingData.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        No process steps available for mapping
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {mappingData.map((processStepMapping) => {
        const isExpanded = expandedSteps.has(processStepMapping.processStep.id);
        const isAiSuggesting = aiSuggesting.has(processStepMapping.processStep.id);
        const riskLevel = getProcessStepRiskLevel(processStepMapping.riskControlMappings);
        const { effective, total } = getEffectiveControlsCount(processStepMapping.riskControlMappings);
        
        return (
          <div key={processStepMapping.processStep.id} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Summary Header */}
            <div 
              className="bg-white p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleExpanded(processStepMapping.processStep.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    {processStepMapping.processStep.stepNumber}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {processStepMapping.processStep.processStepName}
                    </h3>
                    <p className="text-sm text-gray-600 truncate max-w-md">
                      {processStepMapping.processStep.description}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  {/* Quick AI Suggest Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSuggestControls(processStepMapping);
                    }}
                    disabled={isAiSuggesting}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-purple-50 text-purple-600 rounded hover:bg-purple-100 transition-colors disabled:opacity-50"
                  >
                    <Brain className="w-4 h-4" />
                    {isAiSuggesting ? 'Suggesting...' : 'AI Suggest'}
                  </button>
                  
                  {/* Status Indicators */}
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      riskLevel === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                      riskLevel === 'HIGH' ? 'bg-orange-100 text-orange-800' :
                      riskLevel === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {riskLevel} RISK
                    </span>
                    <span className="text-gray-500">
                      {processStepMapping.summary.totalRisks} Risks
                    </span>
                    <span className="text-gray-500">
                      {effective}/{total} Controls Effective
                    </span>
                  </div>
                  
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
            </div>

            {/* Detailed View */}
            {isExpanded && (
              <div className="border-t border-gray-200 bg-gray-50 p-6">
                <ProcessStepDetailCard
                  processStep={{
                    id: processStepMapping.processStep.id,
                    stepNumber: processStepMapping.processStep.stepNumber,
                    name: processStepMapping.processStep.processStepName,
                    description: processStepMapping.processStep.description,
                    type: processStepMapping.processStep.processStepType,
                    riskLevel: riskLevel,
                    effectiveControls: effective,
                    totalControls: total
                  }}
                  risks={convertMappingsToRisks(processStepMapping.riskControlMappings)}
                  controls={convertMappingsToControls(processStepMapping.riskControlMappings)}
                  onEditStep={() => handleEditStep(processStepMapping.processStep.id)}
                  onAddRisk={() => handleAddRisk(processStepMapping.processStep.id)}
                  onAddControl={() => handleAddControl(processStepMapping.processStep.id)}
                  onSuggestControls={() => handleSuggestControls(processStepMapping)}
                  onViewAuditTrail={() => handleViewAuditTrail(processStepMapping.processStep.id)}
                  onUpdateRisk={(riskId, updates) => handleUpdateRisk(processStepMapping.processStep.id, riskId, updates)}
                  onUpdateControl={(controlId, updates) => handleUpdateControl(processStepMapping.processStep.id, controlId, updates)}
                  onDeleteRisk={(riskId) => handleDeleteRisk(processStepMapping.processStep.id, riskId)}
                  onDeleteControl={(controlId) => handleDeleteControl(processStepMapping.processStep.id, controlId)}
                  isAddingRisk={addingRisk.has(processStepMapping.processStep.id)}
                  isAddingControl={addingControl.has(processStepMapping.processStep.id)}
                  isSuggestingControls={aiSuggesting.has(processStepMapping.processStep.id)}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};